import React, { useState } from 'react';

import { Flex, Input, Table } from 'antd';
import { type FilterValue, type SorterResult, type TablePaginationConfig } from 'antd/lib/table/interface';
import { createAsyncSelect, Entity } from 'components/AsyncSelect';
import { type Role, type User } from 'models/user';
import { useAppDispatch, useAppSelector } from 'services/redux/configureStore';
import { setElemsPerPage } from 'services/redux/pages/slice';
import { teamsApi } from 'services/rtk/teams';
import { usersApi } from 'services/rtk/users';

import { getUsersManagementColumns } from './constants/usersManagementColumns';
import styles from './UsersTableManagement.module.css';

const { Search } = Input;

const TeamSelect = createAsyncSelect(teamsApi.useFetchTeamsQuery);
const ManagerSelect = createAsyncSelect(usersApi.useFetchUsersQuery, {
  roles: ['HEAD', 'TEAM_LEAD', 'PARTNER'],
});

export const UsersTableManagement = () => {
  const dispatch = useAppDispatch();

  const { elemsPerPage } = useAppSelector((state) => state.pages['users']);

  const [searchUser, setSearchUser] = useState<string>('');
  const [manager, setManager] = useState<Entity>(null);
  const [team, setTeam] = useState<Entity>(null);

  const [page, setPage] = useState(1);

  const [sorter, setSorter] = useState<{
    orderBy?: string;
    sort?: 'asc' | 'desc' | undefined;
  }>({ orderBy: undefined, sort: undefined });

  const [rolesFilters, setRolesFilters] = useState<Role[] | undefined>(undefined);

  const fetchUsersResponse = usersApi.useFetchUsersQuery({
    search: searchUser || undefined,
    managerId: manager?.id || undefined,
    teamId: team?.id || undefined,
    roles: rolesFilters || undefined,
    statuses: ['ACTIVE'],
    page,
    limit: elemsPerPage,
    ...sorter,
  });

  const usersDataSource = fetchUsersResponse.data?.data.map((user) => ({ ...user, key: user.id }));

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorting: SorterResult<User> | SorterResult<User>[]
  ) => {
    if (!pagination.current || !pagination.pageSize) return;

    if (pagination.pageSize !== elemsPerPage && pagination.pageSize) {
      setPage(1);
      dispatch(setElemsPerPage({ page: 'users', elemsPerPage: pagination.pageSize }));
    } else if (pagination.current !== page && pagination.current) {
      setPage(pagination.current);
    }

    const roleFilter = filters.role as Role[] | undefined;
    setRolesFilters(roleFilter);

    if (!Array.isArray(sorting)) {
      let sort = undefined;
      if (sorting.order === 'ascend') sort = 'asc' as const;
      if (sorting.order === 'descend') sort = 'desc' as const;

      if (sorter.sort !== sort || sorter.orderBy !== sorting.field) {
        setPage(1);
      }

      setSorter({
        orderBy: sorting.field as string | undefined,
        sort,
      });
    }
  };

  const onTeamChange = (team: Entity) => {
    setPage(1);
    setTeam(team);
  };

  const onTeamClick = (team: Entity) => {
    setManager(null);
    onTeamChange(team);
  };

  const onManagerChange = (manager: Entity) => {
    setPage(1);
    setManager(manager);
  };

  const onManagerClick = (manager: Entity) => {
    setTeam(null);
    onManagerChange(manager);
  };

  const onUserChange = (userName: string) => {
    setPage(1);
    setSearchUser(userName);
  };

  const columns = getUsersManagementColumns({ onTeamClick, onManagerClick });

  return (
    <Flex vertical gap={20}>
      <Flex justify={'space-between'} wrap={'wrap-reverse'} gap={10}>
        <Flex wrap={'wrap-reverse'} gap={10}>
          <TeamSelect
            value={team}
            onChange={onTeamChange}
            placeholder={'Выберите звено'}
            style={{ width: 230 }}
          />
          <ManagerSelect
            value={manager}
            onChange={onManagerChange}
            placeholder={'Выберите руководителя'}
            style={{ width: 260 }}
          />
        </Flex>

        <Search
          onSearch={onUserChange}
          placeholder={'Поиск по пользователю'}
          allowClear
          loading={fetchUsersResponse.isFetching}
          enterButton={'Искать'}
          className={styles.input}
        />
      </Flex>

      <Table
        dataSource={usersDataSource}
        columns={columns}
        showSorterTooltip={false}
        pagination={{
          current: page,
          pageSize: elemsPerPage,
          total: fetchUsersResponse.data?.pagination.totalCount,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
        }}
        onChange={onTableChange}
        loading={fetchUsersResponse.isFetching}
        sortDirections={['ascend', 'descend']}
        scroll={{ x: 700 }}
      />
    </Flex>
  );
};
